import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  checkEmailFormat,
  ROLE_LABELS,
  SUBSCRIPTION_PRICE_EUR_PER_MONTH,
  SUBSCRIPTION_TIERS,
  SUPPORT_HOURS,
  SUPPORT_STATUS_LABELS,
  type DownloadInfo,
  type MeResponse,
  type SubscriptionInfo,
  type SupportRequestInfo,
} from "@handvero/shared";
import { api, ApiFailure, errorText } from "../api";
import { BackupNotice } from "../components/Status";
import { SupportBanner } from "../components/SupportBanner";
import { Notice } from "../components/ui";
import { formatDate } from "../hooks";

export function Kundenbereich() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [inviteMsg, setInviteMsg] = useState<{ kind: "ok" | "bad" | "info"; text: string } | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setMe(await api.get<MeResponse>("/api/me"));
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) navigate("/anmelden", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!me) return <section data-page="kunden" />;

  const { company, user } = me;
  const canInvite = user.role !== "employee";
  const full = company.seatsUsed >= company.employeeLimit;

  async function invite(e: FormEvent) {
    e.preventDefault();
    setInviteMsg(null);
    const pre = checkEmailFormat(inviteEmail);
    if (!pre.ok) return setInviteMsg({ kind: "bad", text: pre.message });
    setBusy(true);
    try {
      await api.post("/api/company/invitations", { email: inviteEmail });
      setInviteMsg({ kind: "ok", text: `Einladung an ${pre.email} verschickt.` });
      setInviteEmail("");
      await load();
    } catch (err) {
      setInviteMsg({ kind: err instanceof ApiFailure && err.code === "seat_limit" ? "info" : "bad", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await api.del(`/api/company/invitations/${id}`).catch(() => undefined);
    await load();
  }

  async function removeMember(id: string, name: string) {
    if (!window.confirm(`${name} wirklich entfernen? Der Zugang wird sofort gesperrt.`)) return;
    await api.del(`/api/company/members/${id}`).catch((err) => setInviteMsg({ kind: "bad", text: errorText(err) }));
    await load();
  }

  async function logout() {
    await api.post("/api/auth/logout").catch(() => undefined);
    navigate("/");
  }

  return (
    <section data-page="kunden">
    <div className="wrap stack-lg">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 16, flexWrap: "wrap" }}>
        <div className="stack" style={{ gap: 8 }}>
          <span className="eyebrow">Kundenbereich</span>
          <h1 style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}>Willkommen, {company.name}</h1>
          <p className="muted">
            Angemeldet als {user.name} ({ROLE_LABELS[user.role]})
          </p>
        </div>
        <button className="btn btn-ghost btn-small" onClick={logout}>
          Abmelden
        </button>
      </div>

      {params.get("neu") && <Notice kind="ok">Ihre E-Mail-Adresse ist bestätigt. Ihr Firmenkonto ist freigeschaltet.</Notice>}

      {canInvite && <SubscriptionBanner />}
      <BackupNotice />
      <SupportBanner support={me.support} />
      {me.superAdmin && (
        <Notice kind="info">
          Sie haben Zugang zum Admin-Bereich. <Link to="/desktop/admin">Admin öffnen</Link>
        </Notice>
      )}
      {me.supportRequests.length > 0 && <SupportCard requests={me.supportRequests} onChanged={load} />}

      <div className="dash">
        <div className="card">
          <h3>HandVero auf dem Handy</h3>
          <p>
            Die Web-App läuft im Browser Ihres Handys. Sie melden sich mit Ihrer E-Mail-Adresse und Ihrem Passwort an, wählen einen Ordner und
            fotografieren direkt hinein.
          </p>
          <Link className="btn btn-primary" to="/app" style={{ justifySelf: "start" }}>
            Web-App öffnen
          </Link>
          <h3 style={{ marginTop: 18 }}>HandVero für Windows</h3>
          <p>Die Software für den PC im Büro. Sie ist in Ihrem Paket enthalten.</p>
          <WindowsDownload />
        </div>

        <div className="card">
          <h3>Ihr Paket</h3>
          <dl className="kv">
            <dt>Paket</dt>
            <dd>{company.planLabel}</dd>
            {company.plan === "founder" && (
              <>
                <dt>Startplatz</dt>
                <dd>Nr. {company.founderNumber} von 100</dd>
                <dt>Kostenlos bis</dt>
                <dd>mindestens {formatDate(company.validUntil)}</dd>
              </>
            )}
            {company.plan === "basic" && (
              <>
                <dt>Kosten</dt>
                <dd>kostenlos, ohne Laufzeitende</dd>
              </>
            )}
            {company.plan === "team" && (
              <>
                <dt>Läuft bis</dt>
                <dd>{formatDate(company.validUntil)}</dd>
              </>
            )}
            <dt>Zugänge</dt>
            <dd>{company.employeeLimit}</dd>
          </dl>
          {company.plan !== "team" && (
            <p className="small muted">
              Abo mit mehr Zugängen ab {SUBSCRIPTION_PRICE_EUR_PER_MONTH} € im Monat: {me.subscriptionBookable ? "jetzt verfügbar." : "demnächst verfügbar."}
            </p>
          )}
        </div>

                <div className="card">
          <h3>Mitarbeiter</h3>
          <div className="meter" aria-hidden="true">
            <i style={{ width: `${Math.min(100, Math.round((company.seatsUsed / company.employeeLimit) * 100))}%` }} />
          </div>
          <p className="small">
            {company.seatsUsed} von {company.employeeLimit} {company.employeeLimit === 1 ? "Zugang" : "Zugängen"} belegt
          </p>
          <ul className="people">
            {me.members.map((m) => (
              <li key={m.id}>
                <span className="who">
                  <span>{m.name}</span>
                  <span>{m.email}</span>
                </span>
                <span style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <span className="muted small">{ROLE_LABELS[m.role]}</span>
                  {user.role === "owner" && m.role !== "owner" && (
                    <button className="btn-link small" onClick={() => removeMember(m.id, m.name)}>
                      Entfernen
                    </button>
                  )}
                </span>
              </li>
            ))}
            {me.invitations.map((i) => (
              <li key={i.id}>
                <span className="who">
                  <span>{i.email}</span>
                  <span>eingeladen, gültig bis {formatDate(i.expiresAt)}</span>
                </span>
                {canInvite && (
                  <button className="btn-link small" onClick={() => revoke(i.id)}>
                    Zurückziehen
                  </button>
                )}
              </li>
            ))}
          </ul>
          {canInvite && !full && (
            <form className="inline-form" onSubmit={invite} noValidate>
              <label htmlFor="inviteEmail" className="sr-only">
                E-Mail des Mitarbeiters
              </label>
              <input
                id="inviteEmail"
                type="email"
                placeholder="E-Mail des Mitarbeiters"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <button className="btn btn-ghost btn-small" type="submit" disabled={busy}>
                Einladen
              </button>
            </form>
          )}
          {canInvite && full && (
            <Notice kind="info">
              Alle Zugänge Ihres Pakets sind belegt. Für mehr Mitarbeiter gibt es {me.subscriptionBookable ? "ein" : "bald ein"} Abo{" "}
              ab {SUBSCRIPTION_PRICE_EUR_PER_MONTH} € im Monat.
            </Notice>
          )}
          {inviteMsg && <Notice kind={inviteMsg.kind}>{inviteMsg.text}</Notice>}
        </div>

        <div className="card stack">
          <h3>Projekte und Fotos</h3>
          <p>
            Projekte anlegen, Ordner aus Vorlagen übernehmen und Fotos direkt in den richtigen Ordner schießen. Am PC sehen Sie alles groß mit
            Ordnerbaum, auf dem Handy fotografieren Sie unterwegs.
          </p>
          <div className="link-row">
            <Link className="btn btn-primary" to="/desktop">
              Am PC öffnen
            </Link>
            <Link className="btn btn-ghost" to="/desktop/handy">
              Handy per QR-Code verknüpfen
            </Link>
          </div>
          <p className="small muted">
            Der QR-Code meldet Ihr Handy ohne Passwort an. Danach im Browser „Zum Home-Bildschirm“ wählen, dann startet HandVero wie eine App.
          </p>
        </div>
      </div>
    </div>
    </section>
  );
}

/** Auffälliger Hinweis oben im Kundenbereich: führt zur Seite, auf der das Abo gewählt und bezahlt wird. Erst sichtbar, wenn die Buchung freigegeben ist. */
function SubscriptionBanner() {
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  useEffect(() => {
    api.get<SubscriptionInfo>("/api/subscription").then(setInfo, () => setInfo(null));
  }, []);
  if (!info || !info.bookable || !info.configured) return null;
  const cur = info.current && (info.current.status === "active" || info.current.status === "canceled") ? info.current : null;
  return (
    <div className="card abo-banner">
      <div className="stack" style={{ gap: 6 }}>
        <h2 style={{ fontSize: "1.5rem" }}>{cur ? "Ihr Abo" : "Mehr aus HandVero machen"}</h2>
        <p>
          {cur
            ? `${SUBSCRIPTION_TIERS.find((t) => t.id === cur.tier)?.name}, ${cur.employees} Mitarbeiter, ${cur.status === "canceled" ? "gekündigt, läuft noch bis" : "läuft bis"} ${formatDate(cur.validUntil)}.`
            : "Mit einem Abo bekommen Sie mehr Mitarbeiter sowie Angebote, Aufträge, Rechnungen und Unterweisungen."}
        </p>
      </div>
      <Link className="btn btn-primary btn-big" to="/kundenbereich/abo">
        {cur ? "Zahlungen und Kündigung" : "Paket wählen und bezahlen"}
      </Link>
    </div>
  );
}

const when = (iso: string) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Anfragen des HandVero-Supports: erlauben (befristet), ablehnen oder laufenden Zugang beenden. */
function SupportCard({ requests, onChanged }: { requests: SupportRequestInfo[]; onChanged: () => Promise<void> }) {
  const [hours, setHours] = useState<number>(24);
  const [error, setError] = useState("");
  const current = requests.find((r) => r.status === "requested" || r.active);
  const history = requests.filter((r) => r !== current);

  async function act(url: string, body: object = {}) {
    setError("");
    try {
      await api.post(url, body);
      await onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="card stack support-card" id="support">
      <h3>Zugang für den HandVero-Support</h3>
      {current?.status === "requested" && (
        <>
          <p>
            Der Support bittet um Zugang zu Ihrem Konto: <b>„{current.reason}“</b>
          </p>
          <p className="small muted">
            Erlauben Sie den Zugang, sieht der Support für die gewählte Zeit, was Sie sehen. Einladen, Mitarbeiter entfernen und Handys verknüpfen kann er
            nicht. Sie können den Zugang jederzeit beenden.
          </p>
          <div className="link-row">
            <select value={hours} onChange={(e) => setHours(Number(e.target.value))} aria-label="Wie lange">
              {SUPPORT_HOURS.map((h) => (
                <option key={h} value={h}>
                  {h === 1 ? "für 1 Stunde" : `für ${h} Stunden`}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={() => void act(`/api/company/support/${current.id}/decision`, { allow: true, hours })}>
              Zugang erlauben
            </button>
            <button className="btn btn-ghost" onClick={() => void act(`/api/company/support/${current.id}/decision`, { allow: false })}>
              Ablehnen
            </button>
          </div>
        </>
      )}
      {current?.active && current.expiresAt && (
        <>
          <p>
            Der Support hat Zugang bis <b>{when(current.expiresAt)}</b>. Grund: „{current.reason}“
            {current.usedAt ? ` Zuerst genutzt am ${when(current.usedAt)}.` : " Bisher noch nicht genutzt."}
          </p>
          <div className="link-row">
            <button className="btn btn-ghost" onClick={() => void act(`/api/company/support/${current.id}/revoke`)}>
              Zugang jetzt beenden
            </button>
          </div>
        </>
      )}
      {error && <Notice kind="bad">{error}</Notice>}
      {history.length > 0 && (
        <ul className="small muted support-history">
          {history.map((r) => (
            <li key={r.id}>
              {when(r.createdAt)}: {SUPPORT_STATUS_LABELS[r.status]}
              {r.usedAt ? ", genutzt" : ""} · „{r.reason}“
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WindowsDownload() {
  const [info, setInfo] = useState<DownloadInfo | null>(null);
  useEffect(() => {
    api.get<DownloadInfo>("/api/downloads").then(setInfo, () => undefined);
  }, []);
  if (!info?.windows) {
    return (
      <p className="small muted">Der Download steht gerade nicht bereit. Bitte versuchen Sie es später noch einmal.</p>
    );
  }
  return (
    <>
      <a className="btn btn-primary" href="/api/downloads/windows" download={info.fileName} style={{ justifySelf: "start" }}>
        Für Windows herunterladen
      </a>
      <p className="small muted">
        Datei öffnen und den Schritten folgen, Administratorrechte sind nicht nötig. Fragt Windows beim ersten Start nach, klicken Sie auf
        „Weitere Informationen“ und dann auf „Trotzdem ausführen“.
      </p>
    </>
  );
}
